import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';

import {
    SESSION_VALIDATED_EVENT,
    sessionEventBus,
} from '@apps/web/lib/event_bus';

const MAX_DELAY_MS = 30 * 1000;
// Pulls up to 50% off the capped base backoff so clients disconnected by the
// same deploy don't all reconnect in the same instant, while the actual delay
// always remains at or below MAX_DELAY_MS.
const RECONNECT_JITTER_RATIO = 0.5;
const ESTABLISHED_RECONNECT_MIN_DELAY_MS = 5 * 1000;
const ESTABLISHED_RECONNECT_MAX_DELAY_MS = 60 * 1000;
const NON_RETRYABLE_CLOSE_CODES = new Set([
    1008, // Policy violation, usually auth/authorization failure.
]);
// "Service restart": the staggered ECS drain closes sockets it registered,
// which lands inside the settle window below and would otherwise read as a
// handshake that never worked. The server announcing its own restart must not
// count against this client's budget. Mirrors the extension's exemption.
const SERVICE_RESTART_CLOSE_CODE = 1012;
// The server accepts every handshake and authenticates afterwards, so `onopen`
// fires on sockets that are about to be rejected. A connection therefore only
// counts as established once it survives this long — below it the handshake
// proves nothing about the session. Production p5 of a healthy connection's
// lifetime is 5.3s, so 3s misclassifies ~2.6% of real connections, while a
// rejection close lands at p50 0ms.
const CONNECTION_ESTABLISHED_AFTER_MS = 3 * 1000;
// A socket that has never once established is not recovering from a blip — it
// is misconfigured, unauthenticated, or pointed at a route that does not
// exist. Retrying it forever is what produced ~5,000 rejected handshakes/hour
// against the API. The budget is wall clock rather than an attempt count:
// against a 30s backoff cap, N attempts can only ever buy N × 30s, and 10
// minutes comfortably outlasts the longest observed gap in accepts during a
// deploy (45s).
const GIVE_UP_AFTER_MS = 10 * 60 * 1000;
// A dropped connection does not always surface as `onclose`. A sleeping laptop,
// a NAT or proxy idle reap, or a VPN flap can leave a socket reporting OPEN
// forever while delivering nothing, so no close code and no retry budget can
// see it — the only evidence is the absence of traffic. Against an endpoint
// that echoes the keepalive, any inbound frame proves the server-to-client
// path still works.
const KEEPALIVE_INTERVAL_MS = 25 * 1000;
// Must match the server's `KEEPALIVE_TOKEN`, and the literal the Chrome
// extension has always sent — the endpoint echoes only this exact frame.
const KEEPALIVE_TOKEN = 'keepalive';
// Per MDN, `close()` accepts only 1000 or an application code in 3000-4999;
// omitting it lets the browser pick something in 1001-1015, which is
// indistinguishable from a transport failure. Naming the reason lets `onclose`
// stagger the reconnect below and makes the cause visible in a close event.
const LIVENESS_TIMEOUT_CLOSE_CODE = 4000;
// Tolerates two missed echoes plus a margin (2 × 25s, rounded up for jitter and
// a slow round trip). Only ever measured while the page is visible: per MDN,
// Chrome checks timers once per second in a backgrounded page and, after five
// minutes hidden, only once per minute — which would put the keepalive that
// should reset this deadline in a race with the deadline itself and fire the
// watchdog against perfectly healthy sockets.
const LIVENESS_TIMEOUT_MS = 70 * 1000;
// Ceiling on one `resolveConnectUrl` call. Generous enough that a slow but
// healthy round trip still connects, short enough that a stuck one frees the
// single-flight lock well inside the backoff cap above.
const CREDENTIAL_TIMEOUT_MS = 10 * 1000;
// How many distinct failure episodes one socket may report. Recovery re-opens
// reporting (see the establish handler) so a genuine second outage stays
// visible, but a socket flapping around the establish threshold must not spend
// the whole error budget on one page.
const MAX_REPORTED_EPISODES = 3;

/** Compute one reconnect delay without coupling acceptance tests to React. */
export function getWebSocketReconnectDelayMs({
    attempts,
    hasEverEstablished,
    random = Math.random,
}: {
    readonly attempts: number;
    readonly hasEverEstablished: boolean;
    readonly random?: () => number;
}): number {
    if (hasEverEstablished) {
        return (
            ESTABLISHED_RECONNECT_MIN_DELAY_MS +
            random() *
                (ESTABLISHED_RECONNECT_MAX_DELAY_MS -
                    ESTABLISHED_RECONNECT_MIN_DELAY_MS)
        );
    }

    const baseDelay = Math.min(
        MAX_DELAY_MS,
        5_000 * Math.pow(2, Math.min(attempts, 3)),
    );
    return baseDelay * (1 - random() * RECONNECT_JITTER_RATIO);
}

/**
 * What one `resolveConnectUrl` attempt produced.
 *
 * `terminal` says retrying cannot succeed with the current session — a
 * revoked identity rather than a blip — and stops the socket the way close code 1008
 * does. It is a returned value rather than a thrown type on purpose: an
 * `instanceof` check across a module boundary fails open under module
 * duplication, and failing open here restores the endless re-mint loop this
 * exists to prevent. A transient failure throws instead and spends the
 * ordinary retry budget.
 */
export type ConnectCredential =
    | { readonly kind: 'url'; readonly url: string }
    | { readonly kind: 'terminal' };

/**
 * The socket URL with its query string dropped.
 *
 * `resolveConnectUrl` appends a live credential as `?token=`, so the resolved
 * URL is credential-bearing and must not reach a log line, a Sentry
 * breadcrumb, or anything else that leaves the browser.
 */
function websocketLogPath(url: string): string {
    try {
        return new URL(url).pathname;
    } catch {
        return '(invalid url)';
    }
}

type LivenessWatchdog = {
    /** An inbound frame arrived: the connection is proven live, restart the clock. */
    readonly noteInboundFrame: () => void;
    /** The page became visible: resume probing and re-arm the deadline. */
    readonly resume: () => void;
    /** The page became hidden: stop both timers (see LIVENESS_TIMEOUT_MS). */
    readonly pause: () => void;
    /** The socket is gone: drop every timer this watchdog owns. */
    readonly stop: () => void;
};

/**
 * Detects a socket that stays OPEN while it stops delivering.
 *
 * The protocol has ping/pong for exactly this (MDN: "Pings and Pongs: The
 * Heartbeat of WebSockets", opcodes 0x9/0xA) but browsers do not expose it to
 * JavaScript, so liveness has to be probed at the application layer against an
 * endpoint that echoes the probe.
 *
 * Owns both of its timers so "stop probing" and "resume probing" are single
 * calls rather than a pair that has to be kept in step at each call site — the
 * shape that previously let `resume` re-arm the deadline without restarting the
 * probe, turning a quiet healthy socket into a self-inflicted teardown.
 */
function createLivenessWatchdog({
    socket,
    isPageVisible,
    onTimeout,
}: {
    readonly socket: WebSocket;
    readonly isPageVisible: () => boolean;
    readonly onTimeout: () => void;
}): LivenessWatchdog {
    let keepaliveTimer: NodeJS.Timeout | null = null;
    let livenessTimer: NodeJS.Timeout | null = null;
    // Nothing is probed until the peer proves it answers, so pointing this at an
    // endpoint that stays silent by design degrades to no liveness checking
    // rather than a reconnect loop.
    let hasObservedReply = false;

    const clearTimers = () => {
        if (keepaliveTimer) {
            clearInterval(keepaliveTimer);
            keepaliveTimer = null;
        }
        if (livenessTimer) {
            clearTimeout(livenessTimer);
            livenessTimer = null;
        }
    };

    const armDeadline = () => {
        if (livenessTimer) {
            clearTimeout(livenessTimer);
            livenessTimer = null;
        }
        if (!hasObservedReply || !isPageVisible()) {
            return;
        }
        livenessTimer = setTimeout(onTimeout, LIVENESS_TIMEOUT_MS);
    };

    const startProbing = () => {
        if (keepaliveTimer) {
            return;
        }
        keepaliveTimer = setInterval(() => {
            if (socket.readyState !== WebSocket.OPEN) {
                return;
            }
            try {
                socket.send(KEEPALIVE_TOKEN);
            } catch (error) {
                console.debug('[WebSocket] Keepalive failed:', error);
            }
        }, KEEPALIVE_INTERVAL_MS);
    };

    return {
        noteInboundFrame: () => {
            hasObservedReply = true;
            armDeadline();
        },
        resume: () => {
            if (!isPageVisible()) {
                return;
            }
            startProbing();
            armDeadline();
        },
        pause: clearTimers,
        stop: clearTimers,
    };
}

type UseWebSocketOptions = {
    readonly onClose?: () => void;
    readonly onOpen?: () => void;
    readonly onReconnect?: () => void;
    /**
     * Send periodic keepalives and tear the socket down when nothing comes
     * back. Opt-in, because it is only safe against an endpoint that answers
     * a keepalive: on a server that stays silent by design, the watchdog would
     * reconnect on a loop forever. As a second guard the watchdog only arms
     * once a reply has actually been observed, so pointing this at a silent
     * endpoint — or at an older server mid-rollout — degrades to the old
     * no-liveness behavior instead of looping.
     */
    readonly heartbeat?: boolean;
    /**
     * Called when the watchdog tears down a socket that went silent. Reported
     * by the caller rather than here so this hook keeps no telemetry
     * dependency. Without it a zombie socket is indistinguishable from a quiet
     * one, and the intervals above cannot be tuned against anything but
     * guesswork.
     */
    readonly onLivenessTimeout?: () => void;
    /**
     * Derives the URL to actually connect to, from the stable `url`.
     *
     * For clients that must attach a per-connection credential — the sidepanel
     * mints a single-use WebSocket ticket, which expires in 60s and cannot be
     * replayed — the URL is not knowable ahead of time and must be rebuilt on
     * every attempt.
     *
     * Throwing means the attempt failed transiently and spends the same retry
     * budget a rejected handshake does, so a failing credential endpoint
     * cannot hot-loop. Returning `{ kind: 'terminal' }` stops the socket.
     *
     * Omit it entirely when `url` is already connectable as-is. The sole
     * caller supplies it unconditionally — a credential-free tab returns the
     * URL unchanged — because whether a credential exists is only knowable at
     * connect time, not at render time.
     */
    readonly resolveConnectUrl?: (url: string) => Promise<ConnectCredential>;
    /**
     * This session's credential was rejected outright, so the socket has
     * stopped and only a fresh credential can restart it.
     *
     * The caller is expected to go re-establish that credential — nothing else
     * will. A sidepanel tab sits visible and online for hours, so none of the
     * `online` / `visibilitychange` / `pageshow` re-arms below ever fire for
     * it, and without this hook a rejected ticket left the socket dead until
     * the user signed out and back in.
     *
     * Fires once per terminal rejection, not per retry.
     */
    readonly onTerminalCredential?: () => void;
};

export function useWebSocket(
    url: string | null,
    onMessage: (e: MessageEvent) => void,
    options?: UseWebSocketOptions,
) {
    const heartbeatEnabled = options?.heartbeat === true;
    // Use ref to avoid re-running effect when callback changes
    const onMessageRef = useRef(onMessage);
    const onLivenessTimeoutRef = useRef(options?.onLivenessTimeout);
    const resolveConnectUrlRef = useRef(options?.resolveConnectUrl);
    const onTerminalCredentialRef = useRef(options?.onTerminalCredential);
    useEffect(() => {
        resolveConnectUrlRef.current = options?.resolveConnectUrl;
    }, [options?.resolveConnectUrl]);
    useEffect(() => {
        onTerminalCredentialRef.current = options?.onTerminalCredential;
    }, [options?.onTerminalCredential]);
    useEffect(() => {
        onLivenessTimeoutRef.current = options?.onLivenessTimeout;
    }, [options?.onLivenessTimeout]);
    const onCloseRef = useRef(options?.onClose);
    const onOpenRef = useRef(options?.onOpen);
    const onReconnectRef = useRef(options?.onReconnect);
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);
    useEffect(() => {
        onCloseRef.current = options?.onClose;
    }, [options?.onClose]);
    useEffect(() => {
        onOpenRef.current = options?.onOpen;
    }, [options?.onOpen]);
    useEffect(() => {
        onReconnectRef.current = options?.onReconnect;
    }, [options?.onReconnect]);

    useEffect(() => {
        if (!url) {
            return;
        }

        let ws: WebSocket | null = null;
        let reconnectTimer: NodeJS.Timeout | null = null;
        let establishedTimer: NodeJS.Timeout | null = null;
        let attempts = 0;
        let isUnmounted = false;
        let isPageHidden = false;
        let hasGivenUp = false;
        let hasReportedGaveUp = false;
        let hasReportedCredentialFailure = false;
        let reportedEpisodes = 0;
        let hasEverEstablished = false;
        let firstConnectAtMs: number | null = null;
        // True only while `resolveConnectUrl` is in flight. `ws` is still null
        // across that await, so without this a `resume()` or a fired backoff
        // timer would start a second, concurrent attempt — and each one would
        // burn a single-use ticket.
        let isResolvingConnectUrl = false;
        // The current socket's watchdog, or null when no socket is live. Owned
        // here so the visibility handler below drives it without a second
        // listener, and so teardown is one unconditional call.
        let watchdog: LivenessWatchdog | null = null;

        // Giving up must be visible. Replacing an infinite retry loop with a
        // silently dead socket is a worse failure: the UI looks connected and
        // nothing is arriving. Reported once, because a re-armed socket that
        // keeps failing would otherwise report on every probe.
        // `close` is omitted when no socket ever opened. Synthesizing a close
        // code and `was_clean` for that case would tell an on-call engineer the
        // socket died dirty, when in fact it was never constructed.
        const reportGaveUp = (why: string, close?: CloseEvent) => {
            if (hasReportedGaveUp) {
                return;
            }
            hasReportedGaveUp = true;
            reportedEpisodes++;

            const code = close === undefined ? 'none' : String(close.code);
            console.error(
                `[WebSocket] Giving up (${why}): code=${code} reason=${close?.reason || '(none)'}`,
            );
            Sentry.withScope((scope) => {
                scope.setLevel('warning');
                scope.setTag('websocket_close_code', code);
                scope.setTag('websocket_gave_up_reason', why);
                scope.setContext('websocket', {
                    ...(close === undefined
                        ? {}
                        : {
                              close_reason: close.reason,
                              was_clean: close.wasClean,
                          }),
                    attempts,
                    ever_established: hasEverEstablished,
                });
                Sentry.captureMessage('websocket_gave_up');
            });
        };

        // Reported once per effect. A credential endpoint failing for a socket
        // that has already established can never spend the give-up budget —
        // that budget guards only sockets which never connected — so without
        // this it would retry at the backoff cap forever with no signal at
        // all, which is the silent failure this hook exists to avoid.
        const reportCredentialFailure = (error: unknown) => {
            if (hasReportedCredentialFailure) {
                return;
            }
            hasReportedCredentialFailure = true;
            reportedEpisodes++;

            console.error(
                '[WebSocket] Could not resolve a connection credential:',
                error,
            );
            Sentry.withScope((scope) => {
                scope.setLevel('warning');
                scope.setContext('websocket', {
                    attempts,
                    ever_established: hasEverEstablished,
                    error: String(error),
                });
                Sentry.captureMessage('websocket_credential_failed');
            });
        };

        // Whether this attempt has been superseded and must stop touching
        // shared state. Checked before starting the credential fetch and again
        // after it resolves, so a stale attempt cannot finish opening a socket.
        const shouldAbandonAttempt = () =>
            ws !== null || isUnmounted || isPageHidden || hasGivenUp;

        // A connection attempt that never produced a live socket. Shared by the
        // close handler and the credential-resolution failure path so both
        // spend the same budget: a resolver failure that skipped it would
        // hot-loop against a failing endpoint, which is the exact shape the
        // close-code budget already exists to prevent.
        const hasExhaustedNeverEstablishedBudget = (closeCode: number | null) =>
            !hasEverEstablished &&
            closeCode !== SERVICE_RESTART_CLOSE_CODE &&
            firstConnectAtMs !== null &&
            performance.now() - firstConnectAtMs >= GIVE_UP_AFTER_MS;

        const scheduleReconnect = () => {
            const delay = getWebSocketReconnectDelayMs({
                attempts,
                hasEverEstablished,
            });
            attempts++;

            console.debug(`[WebSocket] Reconnecting in ${delay}ms...`);
            reconnectTimer = setTimeout(() => {
                void connect();
            }, delay);
        };

        const stopWatchdog = () => {
            watchdog?.stop();
            watchdog = null;
        };

        const clearTimers = () => {
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            if (establishedTimer) {
                clearTimeout(establishedTimer);
                establishedTimer = null;
            }
            stopWatchdog();
        };

        // Every way an attempt can end without a live socket. Both callers
        // need the same budget check and the same fallback, and keeping the
        // pair together is what stops one of them drifting.
        const failAttemptWithoutSocket = (why: string) => {
            if (hasExhaustedNeverEstablishedBudget(null)) {
                hasGivenUp = true;
                reportGaveUp(why);
                return;
            }
            scheduleReconnect();
        };

        // A resolver that never settles would hold the single-flight lock
        // forever, and every reconnect path refuses to run behind it — a
        // silently dead socket, which this hook treats as worse than a retry
        // loop. Bounded here rather than inside each resolver so the guarantee
        // belongs to the option's contract instead of to whoever implements it.
        const resolveCredential = async (
            resolve: (url: string) => Promise<ConnectCredential>,
        ): Promise<ConnectCredential> => {
            let timer: NodeJS.Timeout | undefined;
            try {
                return await Promise.race([
                    resolve(url),
                    new Promise<never>((_, reject) => {
                        timer = setTimeout(
                            () =>
                                reject(
                                    new Error(
                                        'Timed out resolving a connection credential',
                                    ),
                                ),
                            CREDENTIAL_TIMEOUT_MS,
                        );
                    }),
                ]);
            } finally {
                clearTimeout(timer);
            }
        };

        const connect = async () => {
            if (isResolvingConnectUrl || shouldAbandonAttempt()) {
                return;
            }
            if (firstConnectAtMs === null) {
                firstConnectAtMs = performance.now();
            }

            let connectUrl = url;
            const resolveConnectUrl = resolveConnectUrlRef.current;
            if (resolveConnectUrl) {
                let resolved: ConnectCredential | null = null;
                isResolvingConnectUrl = true;
                try {
                    resolved = await resolveCredential(resolveConnectUrl);
                } catch (error) {
                    reportCredentialFailure(error);
                } finally {
                    isResolvingConnectUrl = false;
                }
                // The await is a suspension point: an unmount, a page-hide, or
                // a resume that already opened a socket can all have happened
                // while it was pending.
                if (shouldAbandonAttempt()) {
                    return;
                }
                // `undefined` is checked alongside `null`: a resolver that
                // returns nothing must fail here rather than reach `.kind`.
                if (resolved === null || resolved === undefined) {
                    failAttemptWithoutSocket(
                        'never resolved a connection credential',
                    );
                    return;
                }
                if (resolved.kind === 'terminal') {
                    hasGivenUp = true;
                    reportGaveUp(
                        'credential cannot be obtained for this session',
                    );
                    onTerminalCredentialRef.current?.();
                    return;
                }
                connectUrl = resolved.url;
            }

            // Path only: the query string carries a live credential, and
            // Sentry's console breadcrumbs would ship it to a third party on
            // the next captured event — including the give-up report below.
            console.debug(
                '[WebSocket] Connecting:',
                websocketLogPath(connectUrl),
            );
            let socket: WebSocket;
            try {
                socket = new WebSocket(connectUrl);
            } catch {
                // A resolver is caller-supplied, so a malformed URL reaches
                // here. Left unhandled this rejects out of an unawaited async
                // call and the socket dies silently.
                failAttemptWithoutSocket('connection url was not usable');
                return;
            }
            ws = socket;
            const onCloseForSocket = onCloseRef.current;
            const onOpenForSocket = onOpenRef.current;
            const onReconnectForSocket = onReconnectRef.current;
            let isEstablished = false;

            if (heartbeatEnabled) {
                watchdog = createLivenessWatchdog({
                    socket,
                    isPageVisible: () =>
                        !isPageHidden && document.visibilityState === 'visible',
                    onTimeout: () => {
                        console.debug(
                            '[WebSocket] No traffic within liveness window; reconnecting.',
                        );
                        onLivenessTimeoutRef.current?.();
                        // Routes through `onclose`, which owns the backoff and
                        // the reconnect. The code names the reason so that
                        // handler can spread the retry.
                        socket.close(
                            LIVENESS_TIMEOUT_CLOSE_CODE,
                            'Liveness timeout',
                        );
                    },
                });
            }

            ws.onopen = () => {
                // Deliberately silent: the handshake completed, but the server
                // has not yet accepted or rejected the credential. Notifying
                // here would fire an app-wide refetch on every socket the
                // server is about to close — a herd precisely when the auth
                // path is already struggling.
                const isReconnect = hasEverEstablished || attempts > 0;
                establishedTimer = setTimeout(() => {
                    establishedTimer = null;
                    isEstablished = true;
                    hasEverEstablished = true;
                    attempts = 0;
                    // One report per failure episode rather than per page, so
                    // that re-arming does not silently suppress the very
                    // signal used to tell whether recovery works. Capped,
                    // because a socket flapping just past this 3s threshold
                    // would otherwise report on every cycle and flood the
                    // error budget during the outage the trail matters most.
                    if (reportedEpisodes < MAX_REPORTED_EPISODES) {
                        hasReportedGaveUp = false;
                        hasReportedCredentialFailure = false;
                    }
                    console.debug('[WebSocket] Connected');
                    onOpenForSocket?.();
                    if (isReconnect) {
                        onReconnectForSocket?.();
                    }
                    // Started only once the session is real, so a socket the
                    // server is about to reject never probes it.
                    watchdog?.resume();
                }, CONNECTION_ESTABLISHED_AFTER_MS);
            };

            ws.onmessage = (event) => {
                // Any frame proves the connection still delivers, including the
                // server's keepalive echo on an otherwise idle organization. The
                // first one is also what licenses the watchdog to arm at all.
                watchdog?.noteInboundFrame();
                onMessageRef.current(event);
            };

            ws.onerror = () => {
                // The `error` event is spec'd to carry no diagnostic detail, so
                // logging it at error level fills the error budget with a
                // constant. The `close` event that always follows carries the
                // code and reason — that is what gets reported below.
                console.debug('[WebSocket] Error (see close event for cause)');
            };

            ws.onclose = (event) => {
                // A `close` event is a queued task, so it can arrive after
                // this socket was already replaced or deliberately abandoned
                // (see `onPageHide`). Such a socket must still report its own
                // close, but must not touch shared state or schedule work on
                // behalf of the socket that superseded it.
                const isCurrentSocket = ws === socket;
                if (isCurrentSocket) {
                    ws = null;
                    if (establishedTimer) {
                        clearTimeout(establishedTimer);
                        establishedTimer = null;
                    }
                    stopWatchdog();
                }
                if (isEstablished) {
                    onCloseForSocket?.();
                }
                // Neither an unmount nor a page-hide is a failure, so neither
                // may consume the budget or report a give-up.
                if (!isCurrentSocket || isUnmounted || isPageHidden) {
                    return;
                }

                if (NON_RETRYABLE_CLOSE_CODES.has(event.code)) {
                    hasGivenUp = true;
                    reportGaveUp('non-retryable close code', event);
                    // 1008 is the server rejecting this socket's credential
                    // after accepting the handshake — the same terminal
                    // condition the resolver reports, reached from the other
                    // side.
                    onTerminalCredentialRef.current?.();
                    return;
                }

                // Never established, and out of budget. `firstConnectAtMs` is
                // deliberately never reset, so a re-armed socket gets exactly
                // one probe: if it fails without establishing it lands right
                // back here and stops again.
                if (hasExhaustedNeverEstablishedBudget(event.code)) {
                    hasGivenUp = true;
                    reportGaveUp('never established a connection', event);
                    return;
                }

                console.debug(
                    '[WebSocket] Closed:',
                    event.code,
                    event.reason,
                    `wasClean=${event.wasClean}`,
                );

                scheduleReconnect();
            };
        };

        // Neither giving up nor hiding may be permanent. The session provider
        // deliberately preserves a valid session across transient revalidation
        // failures, so `url` stays byte-identical and this effect never
        // re-runs — without these listeners a tab shows a live UI over a dead
        // feed until a full reload.
        const resume = (isDocumentVisible: boolean) => {
            if (isUnmounted) {
                return;
            }
            // Only an event proving the document is back may lift a deliberate
            // close. `online` proves the network returned, not that anyone is
            // looking — and mobile Safari fires `pagehide` on a tab switch
            // without freezing, so letting a network flap lift it would make
            // the failure budget spendable on a hidden page.
            const wasPageHidden = isPageHidden;
            if (isDocumentVisible) {
                isPageHidden = false;
            }
            if (ws || isPageHidden) {
                return;
            }
            if (!wasPageHidden && !hasGivenUp) {
                // An ordinary backoff is pending — don't stampede the server
                // on every tab focus.
                return;
            }
            // Safe to spend the terminal flag only here, where `connect()` can
            // no longer refuse: otherwise an event's one probe is swallowed
            // and nothing ever retries.
            console.debug('[WebSocket] Resuming');
            hasGivenUp = false;
            void connect();
        };
        // `online` cannot prove the document is visible; the other two can.
        const onOnline = () => resume(false);
        // The fourth trigger, and the only one that fires for the sidepanel's
        // normal state: a tab that stays visible and online emits none of the
        // other three, so a socket whose credential failed stayed dead for the
        // page's lifetime even after the session recovered.
        //
        // Checked against the live visibility state rather than `isPageHidden`,
        // which only tracks `pagehide`. An ordinary `visibilitychange` to
        // hidden leaves it false while also revalidating the session, so
        // without this a backgrounded tab would spend its one remaining probe
        // opening a socket nobody is watching.
        const onSessionValidated = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            resume(false);
        };
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                resume(true);
                // Re-arm the watchdog the hidden page deliberately skipped, so
                // a socket that died while backgrounded is caught rather than
                // waiting for traffic that will never come.
                watchdog?.resume();
            } else {
                watchdog?.pause();
            }
        };
        // An open socket can keep the page out of the back/forward cache, so
        // close it on the way out and reopen on restore.
        const onPageHide = () => {
            isPageHidden = true;
            clearTimers();
            if (!ws) {
                return;
            }
            // Drop the reference synchronously. The close event is a queued
            // task a frozen page never runs, and `connect()` refuses while a
            // socket is still held — so leaving it set makes the restore a
            // no-op. The handler stays attached so a late close still reports
            // itself; its identity check keeps it from touching newer state.
            const abandoned = ws;
            ws = null;
            abandoned.close(1000, 'Page hidden');
        };
        const onPageShow = (event: PageTransitionEvent) => {
            // `persisted` distinguishes a bfcache restore, where the socket
            // closed above needs reopening, from an ordinary load where the
            // effect has already connected.
            if (event.persisted) {
                resume(true);
            }
        };

        window.addEventListener('online', onOnline);
        window.addEventListener('pagehide', onPageHide);
        window.addEventListener('pageshow', onPageShow);
        document.addEventListener('visibilitychange', onVisibilityChange);
        sessionEventBus.addEventListener(
            SESSION_VALIDATED_EVENT,
            onSessionValidated,
        );

        void connect();

        return () => {
            isUnmounted = true;
            window.removeEventListener('online', onOnline);
            window.removeEventListener('pagehide', onPageHide);
            window.removeEventListener('pageshow', onPageShow);
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange,
            );
            sessionEventBus.removeEventListener(
                SESSION_VALIDATED_EVENT,
                onSessionValidated,
            );
            clearTimers();
            ws?.close(1000, 'Component unmounted');
        };
    }, [url, heartbeatEnabled]);
}
